import {
  IsIn,
  IsInt,
  IsOptional,
  IsString,
  IsUUID,
  Matches,
  Max,
  MaxLength,
  Min,
  ValidateIf,
} from 'class-validator';

/**
 * Re-inspection appointment booking.
 *
 * - Off unless the municipality turns it on (`inspectionBookingEnabled`,
 *   default false). While off, the portals show nothing and both APIs refuse
 *   every write with `inspection_booking_disabled`.
 * - Officers publish availability windows (weekly or one-off) with a slot
 *   length. An officer opens a re-inspection on a file with the dates a slot
 *   may fall between; a party to the file books, reschedules or cancels within
 *   those dates, or an officer books on their behalf.
 * - All wall-clock times are America/Moncton. Instants travel as ISO strings.
 */

export const BOOKING_TIME_ZONE = 'America/Moncton';

export const BOOKING_CONFIG = {
  /** Slot lengths an officer may choose, in minutes. */
  slotLengths: [15, 30, 45, 60, 90, 120] as const,
  /** A slot must start at least this far ahead to be bookable by an owner. */
  minLeadHours: 12,
  /** How far before the appointment the reminder goes out. */
  reminderLeadHours: 24,
  /** The widest window an officer may open, in days. */
  maxWindowDays: 120,
  /** Times a party may reschedule before they must call the office. */
  maxReschedules: 3,
} as const;

export type InspectionAvailabilityKind = 'weekly' | 'one_off';

export type ReinspectionStatus =
  'awaiting_booking' | 'booked' | 'completed' | 'no_show' | 'withdrawn';

export type ReinspectionBookedBy = 'owner' | 'officer';

export type ReinspectionOutcome = 'completed' | 'no_show';

const DATE = /^\d{4}-\d{2}-\d{2}$/;

/** One availability window as served to the officer. */
export interface InspectionAvailabilityDto {
  id: string;
  localGovernmentId: string;
  officerId: string;
  kind: InspectionAvailabilityKind;
  weekday: number | null;
  date: string | null;
  startMinute: number;
  endMinute: number;
  slotMinutes: number;
}

/** `POST /inspection-booking/availability`. */
export class CreateInspectionAvailabilityDto {
  @IsUUID()
  localGovernmentId!: string;

  @IsIn(['weekly', 'one_off'])
  kind!: InspectionAvailabilityKind;

  @ValidateIf((o: CreateInspectionAvailabilityDto) => o.kind === 'weekly')
  @IsInt()
  @Min(0)
  @Max(6)
  weekday?: number;

  @ValidateIf((o: CreateInspectionAvailabilityDto) => o.kind === 'one_off')
  @Matches(DATE)
  date?: string;

  @IsInt()
  @Min(0)
  @Max(1439)
  startMinute!: number;

  @IsInt()
  @Min(1)
  @Max(1440)
  endMinute!: number;

  @IsIn([...BOOKING_CONFIG.slotLengths])
  slotMinutes!: number;
}

/** A bookable slot. */
export interface ReinspectionSlotDto {
  startsAt: string;
  endsAt: string;
  /** `YYYY-MM-DD` in America/Moncton, for grouping by day. */
  date: string;
}

/** A re-inspection on a file, as an officer sees it. */
export interface ReinspectionBookingDto {
  id: string;
  fileId: string;
  fileReference: string | null;
  fileAddress: string | null;
  localGovernmentId: string;
  officerId: string;
  officerName: string | null;
  status: ReinspectionStatus;
  earliestDate: string;
  latestDate: string;
  startsAt: string | null;
  endsAt: string | null;
  bookedBy: ReinspectionBookedBy | null;
  bookedAt: string | null;
  rescheduleCount: number;
  outcomeNote: string | null;
  outcomeAt: string | null;
  visitId: string | null;
  createdAt: string;
}

/** `GET /files/:fileId/reinspection` (gov). */
export interface FileReinspectionDto {
  enabled: boolean;
  booking: ReinspectionBookingDto | null;
  /** Past rows on the file, newest first. */
  history: ReinspectionBookingDto[];
}

/** `POST /files/:fileId/reinspection` — open booking on a file. */
export class OpenReinspectionDto {
  /** The inspecting officer. Defaults to the caller. */
  @IsOptional()
  @IsUUID()
  officerId?: string;

  @Matches(DATE)
  earliestDate!: string;

  @Matches(DATE)
  latestDate!: string;
}

/** Book or reschedule — the slot's start as an ISO instant. */
export class BookReinspectionSlotDto {
  @IsString()
  @MaxLength(40)
  startsAt!: string;
}

/** `POST /reinspections/:id/outcome`. */
export class RecordReinspectionOutcomeDto {
  @IsIn(['completed', 'no_show'])
  outcome!: ReinspectionOutcome;

  @IsOptional()
  @IsString()
  @MaxLength(2000)
  note?: string;

  @IsOptional()
  @IsUUID()
  visitId?: string;
}

/** The owner's view of the re-inspection on one file (Client Portal). */
export interface CitizenReinspectionDto {
  /** False where the municipality has booking off or nothing is open. */
  available: boolean;
  status: ReinspectionStatus | null;
  earliestDate: string | null;
  latestDate: string | null;
  startsAt: string | null;
  endsAt: string | null;
  /** Whether this owner may book, reschedule or cancel right now. */
  canChange: boolean;
  reschedulesLeft: number;
}

// ---------------------------------------------------------------------------
// Slot computation — shared by gov-api and public-api so both offer the same
// slots and refuse the same ones.
// ---------------------------------------------------------------------------

/** Offset of `zone` from UTC at `instant`, in minutes (Moncton: -180 or -240). */
export function zoneOffsetMinutes(instant: Date, zone: string = BOOKING_TIME_ZONE): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: zone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(instant);
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? '0');
  const asUtc = Date.UTC(
    get('year'),
    get('month') - 1,
    get('day'),
    get('hour') % 24,
    get('minute'),
    get('second'),
  );
  return Math.round((asUtc - Math.floor(instant.getTime() / 1000) * 1000) / 60_000);
}

/** The instant a wall-clock minute on `date` falls at in `zone`. */
export function wallClockToInstant(
  date: string,
  minuteOfDay: number,
  zone: string = BOOKING_TIME_ZONE,
): Date {
  const [y, m, d] = date.split('-').map(Number);
  const naive = Date.UTC(y, m - 1, d, 0, minuteOfDay);
  // Two passes settle the offset either side of a DST change.
  let guess = naive - zoneOffsetMinutes(new Date(naive), zone) * 60_000;
  guess = naive - zoneOffsetMinutes(new Date(guess), zone) * 60_000;
  return new Date(guess);
}

/** `YYYY-MM-DD` of an instant in `zone`. */
export function calendarDateIn(instant: Date, zone: string = BOOKING_TIME_ZONE): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: zone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(instant);
}

function addDays(date: string, days: number): string {
  const [y, m, d] = date.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

function weekdayOf(date: string): number {
  const [y, m, d] = date.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}

export interface SlotWindowInput {
  kind: InspectionAvailabilityKind;
  weekday: number | null;
  date: string | null;
  startMinute: number;
  endMinute: number;
  slotMinutes: number;
}

export interface ComputeSlotsInput {
  windows: SlotWindowInput[];
  earliestDate: string;
  latestDate: string;
  /** Starts already taken for this officer (ISO or Date). */
  taken: Array<{ startsAt: Date | string; endsAt: Date | string }>;
  now: Date;
  /** Minimum lead before a slot may be booked; 0 for officers. */
  minLeadHours?: number;
}

/**
 * Every free slot between `earliestDate` and `latestDate` inclusive, soonest
 * first. A slot overlapping a taken booking is dropped, as is one starting
 * before `now + minLeadHours`. Overlapping windows on the same day yield each
 * start once.
 */
export function computeReinspectionSlots(input: ComputeSlotsInput): ReinspectionSlotDto[] {
  const lead = (input.minLeadHours ?? 0) * 3_600_000;
  const cutoff = input.now.getTime() + lead;
  const taken = input.taken.map((t) => ({
    start: new Date(t.startsAt).getTime(),
    end: new Date(t.endsAt).getTime(),
  }));
  const seen = new Set<number>();
  const out: ReinspectionSlotDto[] = [];
  if (input.earliestDate > input.latestDate) return out;
  for (
    let day = input.earliestDate, guard = 0;
    day <= input.latestDate && guard < 400;
    day = addDays(day, 1), guard++
  ) {
    const wd = weekdayOf(day);
    for (const w of input.windows) {
      const applies = w.kind === 'weekly' ? w.weekday === wd : w.date === day;
      if (!applies || w.slotMinutes <= 0) continue;
      for (let m = w.startMinute; m + w.slotMinutes <= w.endMinute; m += w.slotMinutes) {
        const start = wallClockToInstant(day, m);
        const end = wallClockToInstant(day, m + w.slotMinutes);
        const s = start.getTime();
        if (s < cutoff || seen.has(s)) continue;
        if (taken.some((t) => s < t.end && end.getTime() > t.start)) continue;
        seen.add(s);
        out.push({ startsAt: start.toISOString(), endsAt: end.toISOString(), date: day });
      }
    }
  }
  return out.sort((a, b) => a.startsAt.localeCompare(b.startsAt));
}

/** Whether `[earliest, latest]` is a valid window opened `today`. */
export function validReinspectionWindow(
  earliestDate: string,
  latestDate: string,
  today: string,
): boolean {
  if (!DATE.test(earliestDate) || !DATE.test(latestDate)) return false;
  if (earliestDate > latestDate || latestDate < today) return false;
  return latestDate <= addDays(earliestDate, BOOKING_CONFIG.maxWindowDays);
}
